import {
  RegistrationStructuredTableMetadata,
  RegistrationStructuredTableRowMetadata,
  RegistrationTableInsertType,
} from 'notebook/integrations/registry/RegistrationTableConstants';
import {AntibodyDomainColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {CustomResolutionQueryType} from 'notebook/tables/string-resolution/StringResolutionConstants';
import {BaseTypedValueType, CustomEvalResult, TypedValue} from 'notebook/tables/TableConstants';
import {ResolutionQueryType} from 'notebook/tables/TableStoreTransforms';
import {isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {
  makeTypedValueFromCustomResolvedValue,
  ResolveQueryFn,
} from 'notebook/tables/typed-values/TypedValueResolution';

/**
 * Custom eval function for antibody domains
 * If the table type is REGISTER_EXISTING_ENTITIES, this function will look up the current value and populate
 * the cell.
 * @param typedValue - the typedValue of the current cell
 * @param columnDefinition - the current column the typedValue is in
 * @param cellDependencies - an array containing the existing bioentity for the row
 * @param metadata - the Registration table's metadata
 */
export function antibodyDomainColumnCustomEval(
  typedValue: TypedValue,
  columnDefinition: AntibodyDomainColumnDefinition,
  cellDependencies: Array<TypedValue | null>,
  {
    tableMetadata,
  }: {
    tableMetadata: RegistrationStructuredTableMetadata;
    rowMetadata: RegistrationStructuredTableRowMetadata;
  },
  resolveFromResolutionQuery: ResolveQueryFn
): CustomEvalResult {
  // Only populate with existing domains if we're registering existing entities, there is an existing
  // bioentity, and the cell is empty.
  if (
    tableMetadata.insertType !== RegistrationTableInsertType.REGISTER_EXISTING_ENTITIES ||
    cellDependencies.length === 0 ||
    !isTypedValueEmpty(typedValue)
  ) {
    return {val: typedValue, error: null, locked: false};
  }
  const bioentityTypedValue = cellDependencies[0]!;
  if (!bioentityTypedValue || bioentityTypedValue.type !== BaseTypedValueType.BIOENTITY) {
    return {val: typedValue, error: null, locked: false};
  }
  const {resolvedValue, error} = resolveFromResolutionQuery(
    typedValue,
    {
      type: ResolutionQueryType.CUSTOM,
      customResolutionQueryType:
        CustomResolutionQueryType.REGISTRATION_TABLE_ANTIBODY_DOMAINS_FROM_EXISTING_BIOENTITY,
      bioentityId: bioentityTypedValue.bioentitySummary.id,
      columnDefinition,
    },
    makeTypedValueFromCustomResolvedValue
  );
  return {val: resolvedValue, error, locked: false};
}
